import { UPLOAD } from '@/config/constants'

export interface ValidationResult {
  valid: boolean
  error?: string
}

export function validateImage(file: File): ValidationResult {
  const allowedTypes = UPLOAD.ALLOWED_IMAGE_TYPES as readonly string[]
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Only JPG and PNG files are allowed',
    }
  }

  if (file.size > UPLOAD.MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size must be less than ${UPLOAD.MAX_FILE_SIZE / (1024 * 1024)}MB`,
    }
  }

  return { valid: true }
}

/** Human text for the HTTP statuses this admin actually surfaces to people. */
const STATUS_MESSAGES: Record<number, string> = {
  400: 'Something in that request was not valid. Check the form and try again.',
  401: 'Incorrect email or password.',
  403: "You don't have access to that.",
  404: "We couldn't find that.",
  409: 'That conflicts with something that already exists.',
  413: 'That file is too large.',
  429: 'Too many attempts. Wait a moment and try again.',
  500: 'Something went wrong on our end. Try again shortly.',
  502: 'Something went wrong on our end. Try again shortly.',
  503: 'The service is briefly unavailable. Try again shortly.',
  504: 'That took too long. Try again shortly.',
}

/**
 * Turn whatever was thrown into something worth showing a person.
 *
 * The reason this is more than `error.message`: axios sets that to
 * "Request failed with status code 401", and it was being rendered verbatim on
 * the sign-in screen. Someone mistyping their password was told about an HTTP
 * status code — true, unactionable, and not their language.
 *
 * Order of preference: whatever the API said (it has the most context), then
 * human text for the status, and only then the raw message.
 */
export function getErrorMessage(error: unknown): string {
  const axiosLike = error as {
    response?: { status?: number; data?: { message?: string; error?: string } }
    message?: string
  } | null

  const fromServer =
    axiosLike?.response?.data?.message ?? axiosLike?.response?.data?.error
  if (typeof fromServer === 'string' && fromServer.trim()) {
    return fromServer
  }

  const status = axiosLike?.response?.status
  if (typeof status === 'number' && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status]
  }

  if (error instanceof Error) {
    // A transport failure has no response at all, and axios still words it for
    // developers ("Network Error").
    if (/network error/i.test(error.message)) {
      return "Couldn't reach the server. Check your connection and try again."
    }
    // Don't leak axios's status-code phrasing when we had no better source.
    if (/^Request failed with status code/i.test(error.message)) {
      return 'That request failed. Try again shortly.'
    }
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unexpected error occurred'
}

